import React, { useEffect, useState } from 'react';
import {
  ArrowRight,
  EnvelopeFill,
  LockFill,
  PeopleFill,
  Person,
  ShieldFill,
  X,
} from 'react-bootstrap-icons';
import axiosInstance from '../../api/api';
import { BASE_URL } from '../../config/api-routes';
import {
  writePublicViewerSession,
  type PublicViewerSession,
} from '../../utils/publicViewerSession';
import RecruiterAccessPolicyModal from './RecruiterAccessPolicyModal';
import './PublicVisitorIdentifyModal.css';

type Props = {
  show: boolean;
  username: string;
  onIdentified: (session: PublicViewerSession) => void;
};

export default function PublicVisitorIdentifyModal({ show, username, onIdentified }: Props) {
  const [role, setRole] = useState<'recruiter' | 'hiring-manager' | 'visitor'>('recruiter');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<{
    companyName: string;
    isVerifiedRecruiter: boolean;
    viewerType: string;
  } | null>(null);
  const [showPolicyModal, setShowPolicyModal] = useState(false);

  useEffect(() => {
    if (!show) {
      setRole('recruiter');
      setEmail('');
      setLoading(false);
      setError('');
      setResult(null);
      setShowPolicyModal(false);
    }
  }, [show]);

  useEffect(() => {
    if (!show) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [show]);

  const isRecruiterRole = role === 'recruiter' || role === 'hiring-manager';

  const submit = async () => {
    setError('');
    const trimmed = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    try {
      const { data } = await axiosInstance.post(
        `${BASE_URL}u/${encodeURIComponent(username)}/viewer-identify`,
        { email: trimmed, role }
      );
      const companyName = data.companyName || data.company_name || '';
      const isVerifiedRecruiter = Boolean(data.isVerifiedRecruiter ?? data.is_verified_recruiter);
      const viewerType = data.viewerType || data.viewer_type || 'visitor';
      setResult({ companyName, isVerifiedRecruiter, viewerType });
      if (role === 'recruiter' || role === 'hiring-manager') {
        setShowPolicyModal(true);
      }
    } catch {
      setError('Could not verify your email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const continueToProfile = () => {
    const trimmed = email.trim().toLowerCase();
    const session: PublicViewerSession = {
      email: trimmed,
      companyName: result?.companyName || '',
      isVerifiedRecruiter: Boolean(result?.isVerifiedRecruiter),
      viewerType: result?.viewerType || 'visitor',
      identifiedAt: new Date().toISOString(),
    };
    writePublicViewerSession(username, session);
    onIdentified(session);
  };

  const handlePrimaryAction = () => {
    if (result) {
      if (isRecruiterRole) {
        setShowPolicyModal(true);
        return;
      }
      continueToProfile();
      return;
    }
    void submit();
  };

  if (!show) return null;

  return (
    <>
      <RecruiterAccessPolicyModal
        show={showPolicyModal}
        onClose={() => setShowPolicyModal(false)}
        onContinue={() => {
          setShowPolicyModal(false);
          continueToProfile();
        }}
      />
      <div className="visitor-overlay" role="presentation">
        <div
          className="visitor-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="visitor-modal-title"
        >
          <button type="button" className="visitor-close" aria-label="Close" disabled={loading}>
            <X size={22} aria-hidden />
          </button>

          <div className="visitor-hero">
            <div className="visitor-hero-icon" aria-hidden>
              <ShieldFill size={26} />
            </div>
            <h3 id="visitor-modal-title">Before you continue</h3>
            <p>
              Help us keep SkipScreen profiles secure. Let the candidate know who is viewing their
              profile.
            </p>
          </div>

          <div className="visitor-body">
            <span className="visitor-section-label">I am a</span>
            <div className="visitor-role-cards">
              <button
                type="button"
                className={`visitor-role-card${isRecruiterRole ? ' is-selected' : ''}`}
                onClick={() => setRole('recruiter')}
                disabled={Boolean(result)}
              >
                <div className="visitor-role-card__top">
                  <div className="visitor-role-card__icon" aria-hidden>
                    <PeopleFill size={18} />
                  </div>
                  <span className="visitor-role-radio" aria-hidden />
                </div>
                <div className="visitor-role-card__title">Recruiter / Hiring Team</div>
                <p className="visitor-role-card__desc">I represent a company or hiring team</p>
              </button>

              <button
                type="button"
                className={`visitor-role-card${role === 'visitor' ? ' is-selected' : ''}`}
                onClick={() => setRole('visitor')}
                disabled={Boolean(result)}
              >
                <div className="visitor-role-card__top">
                  <div className="visitor-role-card__icon" aria-hidden>
                    <Person size={18} />
                  </div>
                  <span className="visitor-role-radio" aria-hidden />
                </div>
                <div className="visitor-role-card__title">Other visitor</div>
                <p className="visitor-role-card__desc">I&apos;m browsing or exploring</p>
              </button>
            </div>

            <label className="visitor-email-label" htmlFor="visitor-work-email">
              Work email
            </label>
            <span className="visitor-email-hint">This won&apos;t be visible to the candidate.</span>
            <div className="visitor-input-icon">
              <EnvelopeFill size={16} aria-hidden />
              <input
                id="visitor-work-email"
                type="email"
                value={email}
                placeholder="susan@meta.com"
                onChange={(event) => setEmail(event.target.value)}
                disabled={Boolean(result)}
              />
            </div>

            <div className="visitor-trust-banner">
              <LockFill size={16} aria-hidden />
              <span>Your info helps candidates understand and trust their audience.</span>
            </div>

            {error ? <div className="visitor-error">{error}</div> : null}

            {result ? (
              <div className={`visitor-result${result.isVerifiedRecruiter ? ' is-verified' : ''}`}>
                <div>
                  <strong>Email:</strong> {email.trim()}
                </div>
                <div>
                  <strong>Status:</strong>{' '}
                  {result.isVerifiedRecruiter ? 'Verified Recruiter' : 'Unverified Visitor'}
                </div>
                {result.companyName ? (
                  <div>
                    <strong>Company:</strong> {result.companyName}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="visitor-footer">
            <div className="visitor-privacy">
              <ShieldFill size={18} aria-hidden />
              <div>
                <strong>Your privacy is respected.</strong>
                <span>We never share your email.</span>
              </div>
            </div>

            <button
              type="button"
              className="visitor-continue-btn"
              onClick={handlePrimaryAction}
              disabled={loading}
            >
              {loading
                ? 'Checking…'
                : result
                  ? isRecruiterRole
                    ? 'Review policies'
                    : 'View profile'
                  : 'Continue'}
              {!loading && <ArrowRight size={14} aria-hidden />}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
